import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import {
  RenderRichTextData,
  ContentfulRichTextGatsbyReference,
} from 'gatsby-source-contentful/rich-text';
import { useLocalization } from '@plugins/keytrade-localization/useLocalization';

import { Card } from '@keytrade/components-card';

import Image from '@/components/Image';
import Title from '@/components/Title';
import Link, { LinkStyle, LinkType } from './Link';
import { ImageType } from '@/components/Image';
import Paragraph from './Paragraph';
import { generateSlug } from '@/utils/SlugUtils';
import { mediaQuery } from '@/utils/helpers';
import RichText, { RichTextType } from './Richtext';

const CardItem = styled.li`
  display: flex;

  > * {
    flex-grow: 1;
    text-align: start;
  }
`;

const CardInnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;

  ${mediaQuery.md(`
    flex-direction: column;
  `)}
`;

const ImageWrapper = styled.div`
  flex-shrink: 0;
  width: 140px;
  border-radius: 10px 0 0 10px;
  overflow: hidden;
  font-size: 0;

  ${mediaQuery.md(`
    height: 140px;
    width: 100%;
    border-radius: 10px 10px 0 0;
  `)}
`;

const ImageContent = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: none;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  padding: 2.4rem 2.4rem 3rem;

  p {
    flex-grow: 1;
  }
`;

const LinkWrapper = styled.div`
  margin: auto 0 0;
  display: none;

  ${mediaQuery.md(`
    display: block;
  `)}
`;

export type MediumCardData = RichTextType & {
  title: string;
  text: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  link: LinkType;
  image: ImageType;
};

export type Props = MediumCardData;

const MediumCard: React.FC<Props> = (props) => {
  const { link, title, text, image } = props;
  const { navigate } = useLocalization();

  const richTextCustomRenderer = {
    Paragraph: function CustomParagraph(node, children) {
      return (
        <Paragraph color='GreyBlue' size='sm'>
          {children}
        </Paragraph>
      );
    },
  };

  return (
    <CardItem>
      <Card
        fillContainer
        onClick={(e) => {
          if (!link) return;
          if (link.__typename === 'ContentfulExternalLink') {
            e.preventDefault();
            window.open(link.url, '_blank');
          } else {
            e.preventDefault();
            navigate(generateSlug(link.page));
          }
        }}
      >
        <CardInnerWrapper>
          <ImageWrapper>
            <ImageContent {...image} />
          </ImageWrapper>
          <CardInfo>
            <Title size='xs' level='h3' margin='0 0 1rem'>
              {title}
            </Title>
            {text && (
              <RichText
                data={text}
                customComponents={richTextCustomRenderer}
                options={{
                  marginBottom: '2.4rem',
                }}
                parentContentType='MediumCard'
              />
            )}
            <LinkWrapper>
              <Link {...link} linkStyle={LinkStyle.SECONDARY} />
            </LinkWrapper>
          </CardInfo>
        </CardInnerWrapper>
      </Card>
    </CardItem>
  );
};
export default MediumCard;

export const mediumCardFragmentQuery = graphql`
  fragment MediumCardFragment on ContentfulMediumCard {
    __typename

    image {
      gatsbyImageData(width: 336)
      ...ImageFragment
    }
    title
    text {
      ...RichTextFragment
    }
    link {
      ...LinkFragment
    }
  }
`;
